import { Glasses, Frame, IndianRupee, Calendar, StickyNote } from "lucide-react";

const FRAME_MATERIALS = ["Metal", "Plastic", "TR90", "Titanium", "Acetate", "Wood", "Combination"];
const FRAME_SHAPES = ["Rectangle", "Round", "Oval", "Square", "Cat Eye", "Aviator", "Wayfarer", "Rimless", "Half-Rim"];
const LENS_TYPES = ["Single Vision", "Bifocal", "Progressive", "Zero Power", "Reading Only"];
const LENS_MATERIALS = [
  "CR-39 (Plastic)", "Polycarbonate", "High-Index 1.60",
  "High-Index 1.67", "High-Index 1.74", "Trivex", "Glass",
];
const LENS_COATINGS = [
  "Anti-Glare (AR)", "Blue-Cut", "Photochromic",
  "Anti-Reflective", "Hydrophobic", "Scratch Resistant", "None",
];
const LENS_TINTS = ["Clear", "Gray", "Brown", "Green", "Blue", "Yellow", "Custom"];
const LENS_BRANDS = ["Essilor", "Zeiss", "Nikon", "Hoya", "Crizal", "Kodak", "Rodenstock", "Generic"];

function Row({ cols = 3, children }) {
  const grid = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[cols];
  return <div className={`grid grid-cols-1 ${grid} gap-3`}>{children}</div>;
}
function Sel({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <select
        name={name}
        value={value || ""}
        onChange={onChange}
        className="mt-1 w-full h-11 rounded-md border border-slate-300 bg-white px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
function Txt({ label, name, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder || label}
        className="mt-1 w-full h-11 rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
}

export default function NewSpectaclesFields({ data, onChange }) {
  return (
    <div className="rounded-2xl border border-violet-100 bg-violet-50/30 p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-500 grid place-items-center shadow-sm">
          <Glasses size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900">New Spectacles Order</h3>
          <p className="text-xs text-slate-500">Frame + lens details for a complete pair</p>
        </div>
      </div>

      {/* Frame */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Frame size={13} className="text-violet-600" /> Frame Details
        </div>
        <Row cols={3}>
          <Txt label="Frame Model / SKU" name="spec_frame_model" value={data.spec_frame_model} onChange={onChange} placeholder="e.g. Ray-Ban RB2140" />
          <Txt label="Brand" name="spec_frame_brand" value={data.spec_frame_brand} onChange={onChange} />
          <Txt label="Color" name="spec_frame_color" value={data.spec_frame_color} onChange={onChange} placeholder="Black, Gold…" />
          <Sel label="Material" name="spec_frame_material" value={data.spec_frame_material} onChange={onChange} options={FRAME_MATERIALS} />
          <Sel label="Shape" name="spec_frame_shape" value={data.spec_frame_shape} onChange={onChange} options={FRAME_SHAPES} />
          <Txt label="Size (mm)" name="spec_frame_size" value={data.spec_frame_size} onChange={onChange} placeholder="52-18-140" />
        </Row>
      </div>

      {/* Lens */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <Glasses size={13} className="text-violet-600" /> Lens Details
        </div>
        <Row cols={3}>
          <Sel label="Lens Type" name="spec_lens_type" value={data.spec_lens_type} onChange={onChange} options={LENS_TYPES} />
          <Sel label="Material" name="spec_lens_material" value={data.spec_lens_material} onChange={onChange} options={LENS_MATERIALS} />
          <Sel label="Coating" name="spec_lens_coating" value={data.spec_lens_coating} onChange={onChange} options={LENS_COATINGS} />
          <Sel label="Tint" name="spec_lens_tint" value={data.spec_lens_tint} onChange={onChange} options={LENS_TINTS} />
          <Sel label="Brand" name="spec_lens_brand" value={data.spec_lens_brand} onChange={onChange} options={LENS_BRANDS} />
        </Row>
      </div>

      {/* Pricing */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <IndianRupee size={13} className="text-violet-600" /> Pricing
        </div>
        <Row cols={3}>
          <Txt label="Frame Cost (₹)" name="spec_frame_cost" value={data.spec_frame_cost} onChange={onChange} type="number" placeholder="0" />
          <Txt label="Lens Cost (₹)" name="spec_lens_cost" value={data.spec_lens_cost} onChange={onChange} type="number" placeholder="0" />
          <Txt label="Total (₹)" name="spec_total" value={data.spec_total} onChange={onChange} type="number" placeholder="0" />
          <Txt label="Advance Paid (₹)" name="spec_advance" value={data.spec_advance} onChange={onChange} type="number" placeholder="0" />
          <Txt label="Balance (₹)" name="spec_balance" value={data.spec_balance} onChange={onChange} type="number" placeholder="0" />
          <Txt label="Delivery Date" name="spec_delivery_date" value={data.spec_delivery_date} onChange={onChange} type="date" />
        </Row>
      </div>

      {/* Notes */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <StickyNote size={13} className="text-violet-600" /> Order Notes
        </label>
        <textarea
          rows={2}
          name="spec_notes"
          value={data.spec_notes || ""}
          onChange={onChange}
          placeholder="Any custom fitting or accessory requests…"
          className="mt-1.5 w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </div>
    </div>
  );
}
